"use client";

import { useEffect } from "react";
import Link from "next/link";

import { Button } from "@/components/ui/button";

/**
 * Root segment error boundary for public (and any non-admin) routes.
 *
 * Mirrors `src/app/admin/error.tsx`: the thrown message is for the console
 * only. A Convex or server error can carry a guest phone number, email, or
 * stack path — none of that belongs on a guest-facing screen.
 */

export const ROOT_ERROR_LOG_PREFIX = "[root-error-boundary]";

export type RootErrorReport = {
  digest: string | null;
  message: string;
  name: string;
  pathname: string;
};

export function rootErrorDigest(error: unknown): string | null {
  if (typeof error !== "object" || error === null) return null;
  const { digest } = error as { digest?: unknown };
  if (typeof digest !== "string") return null;
  const trimmed = digest.trim();
  return trimmed === "" ? null : trimmed;
}

// The default export below is named for Next's `error.tsx` convention and
// would shadow the global `Error` constructor if helpers used `instanceof
// Error` later in the file. Always bind the runtime constructor first.
const RuntimeError = globalThis.Error;

function errorMessageAndName(error: unknown): { message: string; name: string } {
  if (error instanceof RuntimeError) {
    return { message: error.message, name: error.name };
  }
  // Cross-realm / instrumented Errors can fail `instanceof` while still
  // carrying a message (and a Next `digest`). Prefer those fields over
  // `String(error)`, which would re-prefix "Error: " into the console payload.
  if (typeof error === "object" && error !== null) {
    const record = error as Record<string, unknown>;
    const message = record.message;
    if (typeof message === "string") {
      const name = record.name;
      return {
        message,
        name: typeof name === "string" ? name : "Error",
      };
    }
  }
  return { message: String(error), name: typeof error };
}

export function buildRootErrorReport(
  error: unknown,
  pathname: string,
): RootErrorReport {
  const { message, name } = errorMessageAndName(error);
  return {
    digest: rootErrorDigest(error),
    message,
    name,
    pathname,
  };
}

export default function RootError({
  error,
  reset,
}: {
  error: InstanceType<typeof RuntimeError> & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    const report = buildRootErrorReport(
      error,
      typeof window === "undefined" ? "" : window.location.pathname,
    );
    console.error(ROOT_ERROR_LOG_PREFIX, report);
  }, [error]);

  const digest = rootErrorDigest(error);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <h2 className="text-2xl font-semibold">Something went wrong</h2>
      <p className="max-w-md text-muted-foreground">
        This page hit an unexpected error. Anything you already submitted is
        safe. Try again, or go back to the home page.
      </p>
      {digest ? (
        <p className="text-xs text-muted-foreground">
          Reference: <span className="font-mono">{digest}</span>
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button type="button" onClick={() => reset()}>
          Try again
        </Button>
        <Button asChild variant="outline">
          <Link
            href="/"
            onNavigate={(event) => {
              event.preventDefault();
              // Reload the document instead of retaining the failed route tree.
              window.location.assign(window.location.origin);
            }}
          >
            Go home
          </Link>
        </Button>
      </div>
    </div>
  );
}
